import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { AdminBundle } from '../lib/types';

/**
 * The console's content store.
 *
 * Reloads the whole bundle after every mutation rather than patching local state. The
 * corpus is one small response, and the server owns things the client would otherwise
 * have to guess at — `order` after a reorder, defaults filled in by zod, the slug
 * uniqueness verdict. Guessing those is how an editor ends up showing a save that did
 * not happen.
 */
export function useAdminContent() {
  const [bundle, setBundle] = useState<AdminBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setBundle(await api.adminContent());
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const b = await api.adminContent();
        if (!cancelled) setBundle(b);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load content.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Runs a mutation, surfaces the server's message verbatim on failure, and reloads.
   * The server's wording is better than ours — it knows that weights total 97, or that
   * a track still has four lessons.
   */
  const run = useCallback(
    async (fn: () => Promise<unknown>, successNote?: string): Promise<boolean> => {
      setError(null);
      setNote(null);
      try {
        await fn();
        await reload();
        if (successNote) setNote(successNote);
        return true;
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
        return false;
      }
    },
    [reload],
  );

  return { bundle, loading, error, note, run, setError, setNote };
}
